import { useState } from "react";
import type { Summary } from "./adrTypes";
import { navigate, repoPath, useCurrentRepo } from "./router";
import { useApiResource } from "./useApiResource";

type SortField = "id" | "title" | "status" | "linkedCapabilities";

// ADRIndex is the spec §6.1 ADR index: number, title, status, date, tags
// (omitted — no real ADR has tag metadata, see CLAUDE.md), number of linked
// capabilities, related ADRs. Only 20 real ADRs exist, so — deliberately,
// unlike the 425-row capability register — this stays a plain searchable/
// sortable table: no URL-synced filter state, no preset views, no CSV
// export; the spec doesn't ask for them here and the dataset is too small
// to need them.
export function ADRIndex({ token }: { token: string }) {
  const repo = useCurrentRepo();
  const { status, data, message } = useApiResource<Summary[]>(token, repo, "/adrs");
  const adrs = data ?? [];
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortField>("id");
  const [dir, setDir] = useState<"asc" | "desc">("asc");

  if (status === "loading") {
    return (
      <section className="panel">
        <h2>ADR library</h2>
        <p>Loading…</p>
      </section>
    );
  }
  if (status !== "ready") {
    // "not-found" is unreachable in practice (GET /api/adrs never 404s),
    // but treating any non-ready status as an error page is the safe
    // default rather than silently falling through to an empty table.
    return (
      <section className="panel">
        <h2>ADR library</h2>
        <p className={status === "error" ? "err" : undefined}>{message}</p>
      </section>
    );
  }

  const needle = query.trim().toLowerCase();
  const filtered = needle
    ? adrs.filter((a) => `${a.id} ${a.title} ${a.status}`.toLowerCase().includes(needle))
    : adrs;
  const sorted = [...filtered].sort((a, b) => {
    const av = a[sort];
    const bv = b[sort];
    const cmp = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv));
    const signed = dir === "desc" ? -cmp : cmp;
    return signed !== 0 ? signed : a.id.localeCompare(b.id);
  });

  function toggleSort(field: SortField) {
    if (sort === field) {
      setDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSort(field);
      setDir("asc");
    }
  }

  return (
    <section className="panel register">
      <h2>ADR library</h2>
      <div className="register-toolbar">
        <input
          type="search"
          role="searchbox"
          aria-label="Search ADRs"
          placeholder="Search number, title, status…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <p className="muted-note">
        {sorted.length} of {adrs.length} ADRs
      </p>
      <div className="table-scroll">
        <table className="register-table">
          <thead>
            <tr>
              <th>
                <button type="button" onClick={() => toggleSort("id")}>
                  Number{sort === "id" ? (dir === "asc" ? " ▲" : " ▼") : ""}
                </button>
              </th>
              <th>
                <button type="button" onClick={() => toggleSort("title")}>
                  Title{sort === "title" ? (dir === "asc" ? " ▲" : " ▼") : ""}
                </button>
              </th>
              <th>
                <button type="button" onClick={() => toggleSort("status")}>
                  Status{sort === "status" ? (dir === "asc" ? " ▲" : " ▼") : ""}
                </button>
              </th>
              <th>Date</th>
              <th>
                <button type="button" onClick={() => toggleSort("linkedCapabilities")}>
                  Linked capabilities{sort === "linkedCapabilities" ? (dir === "asc" ? " ▲" : " ▼") : ""}
                </button>
              </th>
              <th>Related ADRs</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((a) => (
              <tr key={a.id} tabIndex={0} onClick={() => navigate(repoPath(repo, "adrs", a.id))}>
                <td className="mono">{a.id}</td>
                <td>{a.title}</td>
                <td>{a.status}</td>
                <td>{a.dates?.[0]?.date ?? "—"}</td>
                <td>{a.linkedCapabilities}</td>
                <td>{(a.relatedAdrs ?? []).join(", ") || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
